/**
 * One to-do line (spec 2026-10-14 §7, amended): the row ticks it, the pencil opens it. The toggle (the
 * circle and the text) and the pencil are SIBLING pressables (never nested), so hits are unambiguous on
 * web and native. Their own boxes are the targets (react-native-web ignores hitSlop): the row's vertical
 * padding sits on them, and each reaches CIRCLE_REACH into the gutter beside the row — the toggle's past
 * the circle, the pencil's past its glyph — so together they fill the row. There is no divider: rows are
 * separated by space alone. Phones and touch browsers show the pencil at rest; on web with a hovering
 * pointer it is laid out on every row but fades in only while the row is hovered or Tab has focused it,
 * so hovering moves nothing. Finished rows fill the circle green (status.success) and pick the check ink
 * via onColor.
 *
 * The circle is two layers in one 24pt box: a ring (the border) under a fill disc (the green fill +
 * checkmark). Completing a row springs the whole circle back up from a dip (the "completion pop").
 * Un-completing drains the fill: the disc shrinks back to that dip size and fades while the ring
 * stays put, then unmounts — the check visibly sinks out of the circle instead of blinking off.
 * Neither layer is a pressable, so the row keeps exactly two top-level pressables (toggle + pencil).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  StyleSheet,
  Animated,
  Easing,
  Platform,
} from 'react-native';
import { Text } from '@/components/common/text';
import Icon from 'react-native-vector-icons/Ionicons';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import { useMotion } from '@/hooks/shared/useMotion';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { useHoverStyle, HOVER_TRANSITION } from '@/hooks/shared/useHoverStyle';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { HighlightPlate } from '@/components/shared/redesign/HighlightPlate';
import { StackingRow } from '@/components/shared/redesign/StackingRow';
import { useFontScale } from '@/hooks/shared/useFontScale';
import { onColor } from '@/constants/shared/theme';
import { describeRepeat } from '@/utils/residents/todoRecurrence';
import { formatTime } from '@/utils/residents/todoTime';
import type { ResidentTodo } from '@/types/residents/todos';

/**
 * How far the circle dips before springing back, and how small the fill gets
 * as it drains. Deep enough to read as a punch at 24pt, shallow enough that
 * the checkmark never looks like it blinks.
 */
const POP_FROM_SCALE = 0.6;
/** The circle. The pop and drain are tuned at this size, and the now line's glow aligns to its left. */
export const CHECKBOX = 24;
/** A large (Planner) row's vertical padding (24 + 26 = a 50pt row). */
export const ROW_PAD_LARGE = 13;
/** How far a row's tap targets reach into the gutter each side of it: the toggle's past the circle on the left,
 *  the pencil's past its glyph on the right. The reach needs a host that doesn't clip at its own edges: the
 *  Planner page doesn't, and a host that does (a ScrollView) must reach this far into its padding and pad its
 *  rows back, as the home list does. Measured on web; native is owed to the device pass. */
export const CIRCLE_REACH = 14;
/** A tap target's least width (Apple's 44pt). */
const TARGET = 44;
/** The pencil glyph. */
const PENCIL = 16;
/** The gap after the time, which is also the pencil box's left padding: with the glyph and its reach into the
 *  gutter, the box is TARGET wide (14 + 16 + 14). */
const PENCIL_PAD = TARGET - PENCIL - CIRCLE_REACH;
/** The unticked ring's weight. */
const RING = 1.5;
/** Web-only CSS (RN's style types don't know it): the hover pencil fades with the hover language's 150ms. */
const PENCIL_FADE = { transition: HOVER_TRANSITION } as any;

/**
 * The checkbox's spoken name: the title, then the time, "late" when it has passed unticked, and the
 * repeat — "Take out trash, 6:30 PM, late, repeats every week on Tuesday" — so neither the red time
 * nor the ↻ glyph is sight-only.
 */
const checkboxLabel = (todo: ResidentTodo, late: boolean): string => {
  const parts = [todo.title];
  if (todo.dueTime) parts.push(formatTime(todo.dueTime));
  if (late) parts.push('late');
  if (todo.repeat) {
    const rule = describeRepeat(todo.repeat, todo.occurrenceDate ?? todo.dueDate);
    parts.push(`repeats ${rule.charAt(0).toLowerCase()}${rule.slice(1)}`);
  }
  return parts.join(', ');
};

/**
 * Keyboard focus only: a click focuses the pencil too (react-native-web renders a focusable div), and a row the
 * pointer has left must not keep its pencil. `:focus-visible` is the browser's call; a browser without it (or
 * native, whose target is a tag) shows the pencil on any focus.
 */
const isKeyboardFocus = (e: { nativeEvent: unknown }): boolean => {
  try {
    return (e.nativeEvent as any)?.target?.matches?.(':focus-visible') ?? true;
  } catch {
    return true;
  }
};

interface TodoRowProps {
  todo: ResidentTodo;
  onToggle: (todo: ResidentTodo) => void;
  onEdit: (todo: ResidentTodo) => void;
  /** Optional right-aligned date (Overdue rows show how old the item is), in the error ink. Takes the time's slot. */
  dateLabel?: string;
  /** Day lists only: the time has passed and the row is unticked — the time takes the error ink. */
  late?: boolean;
  /** 'large' (default): the Planner, a 16pt title. 'small': the home card's no-scroll list, today's
   *  14pt title and padding so it fits the same rows. */
  size?: 'large' | 'small';
}

export const TodoRow: React.FC<TodoRowProps> = React.memo(
  ({ todo, onToggle, onEdit, dateLabel, late = false, size = 'large' }) => {
    const type = useTypography();
    const space = useSpacing();
    const radius = useRadius();
    const motion = useMotion();
    const reduceMotion = useReducedMotion();
    const { isLargeText } = useFontScale();

    const popScale = useRef(new Animated.Value(1)).current;
    // 1 = the fill whole, 0 = drained away. Drives the disc's opacity and scale.
    const fillProgress = useRef(new Animated.Value(1)).current;
    const fillScale = useMemo(
      () =>
        fillProgress.interpolate({
          inputRange: [0, 1],
          outputRange: [POP_FROM_SCALE, 1],
        }),
      [fillProgress],
    );
    // Tracks the previous `completed` so the pop plays on the flip, not on
    // mount — otherwise every already-done row would punch as the list appears.
    const wasCompleted = useRef(todo.completed);

    // The fill outlives `completed` on the way out so it can drain in place.
    // Synced during render, not in an effect, so a check draws the fill in the
    // frame the store flips and reduced motion drops it just as fast; otherwise
    // the drain's end callback clears it.
    const [fillShown, setFillShown] = useState(todo.completed);
    if (todo.completed && !fillShown) setFillShown(true);
    if (!todo.completed && fillShown && reduceMotion) setFillShown(false);

    useEffect(() => {
      const justCompleted = todo.completed && !wasCompleted.current;
      const justUncompleted = !todo.completed && wasCompleted.current;
      wasCompleted.current = todo.completed;

      if (justUncompleted && !reduceMotion) {
        popScale.setValue(1);
        const drain = Animated.timing(fillProgress, {
          toValue: 0,
          duration: motion.duration.fast,
          easing: Easing.bezier(...motion.easing.standard),
          useNativeDriver: true,
        });
        drain.start(({ finished }) => {
          if (finished) setFillShown(false);
        });
        return () => drain.stop();
      }

      // Whole again: a check may interrupt a drain partway, so the fill lands
      // on full size here before anything else happens.
      fillProgress.setValue(1);

      // Reduced motion, or a row that mounted already done: land on the
      // resting size immediately and cancel anything in flight.
      if (!justCompleted || reduceMotion) {
        popScale.setValue(1);
        return;
      }

      // Known quirk, accepted: a background refetch that flips a row to done
      // pops it too. We deliberately do not try to tell that apart from a tap.
      popScale.setValue(POP_FROM_SCALE);
      const pop = Animated.spring(popScale, {
        toValue: 1,
        useNativeDriver: true,
        ...motion.spring.bouncy,
      });
      pop.start();
      return () => pop.stop();
    }, [todo.completed, reduceMotion, popScale, fillProgress, motion]);

    // A finished drain leaves the value at 0. Park it whole once the disc has
    // gone, so the next check mounts the fill full-size from its first frame
    // rather than a frame later when the effect above runs.
    useEffect(() => {
      if (!fillShown) fillProgress.setValue(1);
    }, [fillShown, fillProgress]);

    // One hover plate for the whole row: the toggle and the pencil are two pressables, and tinting
    // each on its own reads as two half-rows. The row View tracks the pointer (mouse events, web
    // only) and draws the plate, which reaches space.sm past the row so the circle and the pencil
    // get air while the row and its content stay put. The pressables keep their cursor and press
    // dip, and the pencil darkens while the pointer is on it.
    const rowHover = useHoverStyle('none');
    // Web with a hovering pointer lays the pencil out on every row but shows it only while the row is
    // hovered or Tab has focused it, so it keeps its slot: hovering never moves the time or re-truncates
    // the title. Touch browsers and native have no hover to reveal it, so it shows at rest.
    const hoverPointer =
      Platform.OS === 'web' &&
      (globalThis as any).window?.matchMedia?.('(hover: hover) and (pointer: fine)')?.matches === true;
    const [pencilFocused, setPencilFocused] = useState(false);
    const pencilShown = !hoverPointer || rowHover.isHovered || pencilFocused;

    // The trailing label: an Overdue row's date, else the row's time.
    const metaLabel = dateLabel ?? (todo.dueTime ? formatTime(todo.dueTime) : undefined);
    // An Overdue row's date says how late it is, so it takes the error ink like a late time. Only `late` says
    // so aloud: the Overdue line above already says it in words.
    const metaLate = late || dateLabel !== undefined;

    const styles = useThemedStyles(({ colors, semantic }) =>
      StyleSheet.create({
        // No padding and no gap on the row: its two pressables stretch to its full height and carry the vertical
        // padding themselves (padLarge / padSmall), so their boxes meet.
        row: {
          flexDirection: 'row',
          alignItems: 'stretch',
        },
        padLarge: {
          paddingVertical: ROW_PAD_LARGE,
        },
        padSmall: {
          paddingVertical: space.lg,
        },
        // The toggle's box: the circle, the 12pt gap and the text. The negative margin cancels the gutter
        // padding, so the circle stays at x 0 while the box reaches CIRCLE_REACH into the gutter.
        toggleTarget: {
          flex: 1,
          minWidth: 0,
          flexDirection: 'row',
          alignItems: 'center',
          gap: space.md,
          paddingLeft: CIRCLE_REACH,
          marginLeft: -CIRCLE_REACH,
        },
        // The pencil's TARGET-wide box: the gap after the time, the glyph, then CIRCLE_REACH into the gutter,
        // whose negative margin keeps the glyph at the row's right edge.
        pencilTarget: {
          justifyContent: 'center',
          paddingLeft: PENCIL_PAD,
          paddingRight: CIRCLE_REACH,
          marginRight: -CIRCLE_REACH,
        },
        checkbox: {
          width: CHECKBOX,
          height: CHECKBOX,
        },
        ring: {
          ...StyleSheet.absoluteFillObject,
          borderRadius: radius.full,
          borderWidth: RING,
          borderColor: semantic.border.control,
        },
        // Green under a whole fill, so the disc's anti-aliased edge never blends against the ring's
        // grey. It flips back with the store — while the disc still covers it — so the drain
        // uncovers the resting ring.
        ringDone: {
          borderColor: semantic.status.success,
        },
        fill: {
          ...StyleSheet.absoluteFillObject,
          borderRadius: radius.full,
          backgroundColor: semantic.status.success,
          alignItems: 'center',
          justifyContent: 'center',
        },
        checkmark: {
          ...type.captionMedium,
          color: onColor(semantic.status.success),
        },
        // Title + time: a row, or a column from 1.3x text (StackingRow).
        text: {
          flex: 1,
          minWidth: 0,
          alignItems: 'center',
          gap: space.md,
        },
        textStacked: {
          alignItems: 'flex-start',
          gap: space.xs,
        },
        // Title + repeat glyph: the glyph sits right after the title and the title gives way first,
        // so the glyph never gets pushed off. What keeps the time from being squeezed is the
        // trailing label's flexShrink 0 (dateLabel): on RN-web a View is already minWidth 0 and a
        // one-line Text is overflow hidden, so the title truncates on its own. The minWidth 0 here
        // and on title is a harmless guarantee.
        titleGroup: {
          minWidth: 0,
          flexShrink: 1,
          flexDirection: 'row',
          alignItems: 'center',
          gap: space.xs,
        },
        titleGroupInRow: {
          flex: 1,
        },
        titleLarge: {
          ...type.bodyLarge,
        },
        titleSmall: {
          ...type.body,
        },
        title: {
          flexShrink: 1,
          minWidth: 0,
          color: colors.text.primary,
        },
        titleDone: {
          color: colors.text.quiet,
          textDecorationLine: 'line-through',
        },
        dateLabel: {
          ...type.small,
          flexShrink: 0,
          color: colors.text.quiet,
          fontVariant: ['tabular-nums'],
        },
        // Clears 4.5:1 on the card and on the page in both themes (themeContrast.test.ts).
        lateLabel: {
          color: semantic.status.errorText,
        },
        repeatIcon: {
          color: colors.text.quiet,
        },
        editIcon: {
          color: colors.text.quiet,
        },
        editIconHovered: {
          color: colors.text.primary,
        },
        // Web with a hovering pointer: laid out but invisible until the row is hovered or Tab focuses the pencil.
        editIconHidden: {
          opacity: 0,
        },
      }),
    );

    // The row's vertical padding for this size; both pressables carry it, so their boxes span the row.
    const padStyle = size === 'small' ? styles.padSmall : styles.padLarge;

    return (
      <View style={[styles.row, rowHover.viewStyle]} {...(rowHover.mouseProps as any)}>
        <HighlightPlate visible={rowHover.isHovered} bleed={space.sm} />
        <Hoverable
          hover="none"
          style={[styles.toggleTarget, padStyle]}
          onPress={() => onToggle(todo)}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: todo.completed }}
          accessibilityLabel={checkboxLabel(todo, late)}
        >
          <Animated.View
            style={[styles.checkbox, { transform: [{ scale: popScale }] }]}
          >
            <View style={[styles.ring, todo.completed && styles.ringDone]} />
            {fillShown ? (
              <Animated.View
                style={[
                  styles.fill,
                  { opacity: fillProgress, transform: [{ scale: fillScale }] },
                ]}
              >
                <Text style={styles.checkmark}>✓</Text>
              </Animated.View>
            ) : null}
          </Animated.View>
          <StackingRow style={styles.text} stackedStyle={styles.textStacked}>
            <View style={[styles.titleGroup, !isLargeText && styles.titleGroupInRow]}>
              <Text
                style={[
                  size === 'small' ? styles.titleSmall : styles.titleLarge,
                  styles.title,
                  todo.completed && styles.titleDone,
                ]}
                numberOfLines={isLargeText ? 2 : 1}
              >
                {todo.title}
              </Text>
              {todo.repeat ? <Icon name="repeat" size={14} style={styles.repeatIcon} /> : null}
            </View>
            {metaLabel ? (
              <Text style={[styles.dateLabel, metaLate && styles.lateLabel]} numberOfLines={1}>
                {metaLabel}
              </Text>
            ) : null}
          </StackingRow>
        </Hoverable>
        <Hoverable
          hover="none"
          style={[styles.pencilTarget, padStyle]}
          onPress={() => onEdit(todo)}
          onFocus={e => setPencilFocused(isKeyboardFocus(e))}
          onBlur={() => setPencilFocused(false)}
          accessibilityRole="button"
          accessibilityLabel={`Edit ${todo.title}`}
        >
          {({ hovered }) => (
            <Icon
              name="pencil-outline"
              size={PENCIL}
              style={[
                styles.editIcon,
                hovered && styles.editIconHovered,
                !pencilShown && styles.editIconHidden,
                hoverPointer ? PENCIL_FADE : null,
              ]}
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
              aria-hidden
            />
          )}
        </Hoverable>
      </View>
    );
  },
);

TodoRow.displayName = 'TodoRow';
